<template>
  <div class="tasks-index">
    <Filters
      :filters="tasksFilters"
      filter-key="tasks"
      :initial-data="filtersStatus.tasks"
      :loading="filtersLoading"
    />

    <TableWrapper
      :table-columns="tasksColumns"
      table-key="tasks"
      row-key="id"
      :showRefreshButton="true"
      @restart="handleTaskRestart"
      @refreshTable="tableRows"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import { tasksColumns, tasksFilters } from '@/config';
  import { tasksApi } from '@/api';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  export default {
    name: 'TasksIndex',
    mixins: [abortControllerMixin, filterLoadingMixin],
    components: {
      TableWrapper,
      Filters,
    },
    data() {
      return {
        tasksColumns,
        tasksFilters,
        initialUserData: {},
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        // Create abort signal for this request
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;

        try {
          const tasksIndex = await tasksApi.index(
            this.tableFilters('tasks'),
            this.filtersStatus.tasks,
            { signal }
          );
          const tasksCount = await tasksApi.count(this.filtersStatus.tasks, {
            signal,
          });
          this.updateTableRows({ tableKey: 'tasks', rows: tasksIndex.data });
          this.updateTableCount({
            tableKey: 'tasks',
            count: tasksCount.data.count,
          });
        } catch (error) {
          // Ignore aborted requests
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }

          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      applyDefaultSorting() {
        this.$store.dispatch('updateTableSorting', {
          tableKey: 'tasks',
          sorting: {
            prop: 'id',
            order: 'descending',
          },
        });
      },
      // TODO: SMP-23 Add my test
      handleTaskRestart(row) {
        this.$confirm(
          this.$t('tasks.restart.message'),
          this.$t('tasks.restart.title', { taskId: row.id }),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
            type: 'info',
          }
        )
          .then(async () => {
            const response = await tasksApi.restart(row.id);
            if (response.status === 200) {
              this.$notify({
                showClose: true,
                type: 'success',
                message: this.$t('tasks.restart.success', { taskId: row.id }),
              });
              this.tableRows();
            }
          })
          .catch((error) => {
            this.$notify({
              showClose: true,
              type: 'error',
              message: error.response.data.error.message,
            });
          });
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.applyDefaultSorting();
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>

<style lang="scss">
  .tasks-index {
    .table-cell-status {
      position: relative;

      @each $modifier, $map in $task-statuses {
        &--#{$modifier} {
          & .cell {
            padding-left: 20px;
          }

          &:after {
            content: '';
            display: block;
            width: 10px;
            position: absolute;
            height: 100%;
            top: 0;
            left: 0;
            background: map-get($map, 'background');
          }
        }
      }
    }
  }
</style>
